import { useCallback, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { buildCoreRequest } from "@/utils/coreRequestBuilder";
import { nowUtc } from "@/utils/appNow";
import { corePost } from "@/services/coreApi";

dayjs.extend(utc);

export default function useRnwAnalyserState({
  voyagePlannerPoints,
  calculatorBasespeed,
  plannerVesselSettings,
  speedProfileData,
  consumptionProfileData,
  defaultSpeedProfileData,
  cwViaAreasGeojson,
  applyRnwSafety,
  rnwLegs,
  setRnwLegs,
  accessContext,
  setAccessContext,
}) {
  const [switchCombinedRnw, setSwitchCombinedRnw] = useState(false);
  const [selectedMetrics, setSelectedMetrics] = useState({
    Usage: true,
    Navigability: false,
    airDraft: false,
    Usability: false,
    ViaAreas: false,
  });
  const [isFetchingAnalyser, setIsFetchingAnalyser] = useState(false);
  // True from a successful fetch until the leaflet layer is actually on the
  // map (the overlay reports back via onLayerRendered) — the run spinner
  // covers both phases, since rendering the huge geojson takes time too.
  const [isRenderingLayer, setIsRenderingLayer] = useState(false);
  const [showCachedAnalyserToast, setShowCachedAnalyserToast] = useState(false);
  const [geojsonVersion, setGeojsonVersion] = useState(0);

  const analyserRequestIdRef = useRef(0);
  const [loadedAnalyserKey, setLoadedAnalyserKey] = useState(null);
  const analyserInFlightKeyRef = useRef(null);

  const analyserSpeedProfileData = useMemo(
    () =>
      Array.isArray(speedProfileData) && speedProfileData.length > 0
        ? speedProfileData
        : defaultSpeedProfileData,
    [defaultSpeedProfileData, speedProfileData],
  );

  const analyserRequestData = useMemo(
    () =>
      buildCoreRequest({
        algorithm: "analyser",
        voyagePlannerPoints,
        calculatorDepartureTime: nowUtc().format("YYYY-MM-DDTHH:mm:ss[+00:00]"),
        calculatorBasespeed,
        plannerVesselSettings,
        speedProfileData: analyserSpeedProfileData,
        consumptionProfileData,
        windLimit: plannerVesselSettings?.windLimit,
        waveLimit: plannerVesselSettings?.waveLimit,
        currentLimit: plannerVesselSettings?.currentLimit,
        cwViaAreasGeojson,
        applyRnwSafety,
      }),
    [
      analyserSpeedProfileData,
      applyRnwSafety,
      calculatorBasespeed,
      consumptionProfileData,
      cwViaAreasGeojson,
      plannerVesselSettings,
      voyagePlannerPoints,
    ],
  );

  const analyserRequestKey = useMemo(
    () =>
      // Without planner points: the analyser result only depends on vessel and network.
      JSON.stringify({
        algorithm: "analyser",
        calculatorBasespeed,
        plannerVesselSettings,
        speedProfileData: analyserSpeedProfileData,
        consumptionProfileData,
        windLimit: plannerVesselSettings?.windLimit,
        waveLimit: plannerVesselSettings?.waveLimit,
        currentLimit: plannerVesselSettings?.currentLimit,
        cwViaAreasGeojson,
        applyRnwSafety,
      }),
    [
      analyserSpeedProfileData,
      applyRnwSafety,
      calculatorBasespeed,
      consumptionProfileData,
      cwViaAreasGeojson,
      plannerVesselSettings,
    ],
  );

  const fetchRnwLegs = useCallback(async () => {
    if (loadedAnalyserKey === analyserRequestKey) {
      setShowCachedAnalyserToast(true);
      return;
    }
    if (analyserInFlightKeyRef.current === analyserRequestKey) return;

    const requestId = analyserRequestIdRef.current + 1;
    analyserRequestIdRef.current = requestId;
    analyserInFlightKeyRef.current = analyserRequestKey;
    setIsFetchingAnalyser(true);

    try {
      const responseRaw = await corePost(
        "/services/meteo-analyser",
        analyserRequestData,
        accessContext,
        setAccessContext,
      );
      if (analyserRequestIdRef.current !== requestId) return;

      const responseData =
        typeof responseRaw === "string" ? JSON.parse(responseRaw) : responseRaw;

      setLoadedAnalyserKey(analyserRequestKey);
      setIsRenderingLayer(true);
      setRnwLegs(responseData);
      setGeojsonVersion((prev) => prev + 1);
    } catch (error) {
      console.error("fetchRnwLegs() error", error);
    } finally {
      if (analyserRequestIdRef.current === requestId) {
        analyserInFlightKeyRef.current = null;
        setIsFetchingAnalyser(false);
      }
    }
  }, [
    accessContext,
    analyserRequestData,
    analyserRequestKey,
    loadedAnalyserKey,
    setAccessContext,
    setRnwLegs,
  ]);

  const clearAnalyserData = useCallback(() => {
    analyserRequestIdRef.current += 1;
    setLoadedAnalyserKey(null);
    analyserInFlightKeyRef.current = null;
    setShowCachedAnalyserToast(false);
    setRnwLegs(null);
    setGeojsonVersion((prev) => prev + 1);
    setIsFetchingAnalyser(false);
    setIsRenderingLayer(false);
  }, [setRnwLegs]);

  const handleLayerRendered = useCallback(() => {
    setIsRenderingLayer(false);
  }, []);

  const handleMetricChange = useCallback(
    (event) => {
      const { name } = event.target;
      if (switchCombinedRnw) {
        setSelectedMetrics((prev) => ({
          ...prev,
          [name]: !prev[name],
        }));
      } else {
        setSelectedMetrics({
          Usage: false,
          Navigability: false,
          airDraft: false,
          Usability: false,
          ViaAreas: false,
          [name]: !selectedMetrics[name],
        });
      }
    },
    [selectedMetrics, switchCombinedRnw],
  );

  const handleCombinedToggle = useCallback((event) => {
    const checked = event.target.checked;
    setSwitchCombinedRnw(checked);
    if (checked) {
      setSelectedMetrics((prev) => ({
        ...prev,
        Usage: false,
      }));
    } else {
      setSelectedMetrics({
        Usage: true,
        Navigability: false,
        airDraft: false,
        Usability: false,
        ViaAreas: false,
      });
    }
  }, []);

  const isAnalyserResultStale =
    loadedAnalyserKey !== null && loadedAnalyserKey !== analyserRequestKey;

  return {
    clearAnalyserData,
    hasAnalyserResult: rnwLegs != null,
    isAnalyserResultStale,
    fetchRnwLegs,
    geojsonVersion,
    handleCombinedToggle,
    handleLayerRendered,
    handleMetricChange,
    isFetchingAnalyser,
    isRenderingLayer,
    selectedMetrics,
    setShowCachedAnalyserToast,
    showCachedAnalyserToast,
    switchCombinedRnw,
  };
}
